import { Box, Typography } from "@mui/material";
import * as chartUtils from "chartUtils";
import * as logger from "common/logger";
import * as numbers from "common/numbers";
import * as labels from "config.labels";
import { connect } from "react-redux";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as peersSelectors from "store/selectors/peers/peersSelectors";

const COLORS = [
  "#FF634E",
  "#FFAB53",
  "#4CAF50",
  "#2196F3",
  "#9C27B0",
  "#FF5722",
  "#607D8B",
  "#795548",
  "#E91E63",
  "#00BCD4",
];

export const parseData = (peers) => {
  if (!peers || !peers.length) return { data: [], validIndices: new Set() };

  const layersData = peers
    .map((peer, index) => {
      if (!peer.loaded || !peer.layers || !peer.layers.length) return [];

      try {
        return peer.layers.map((layer) => {
          const attachment = numbers.parseFloatOrNull(layer.attachment) || 0;
          const limit = numbers.parseFloatOrNull(layer.limit) || 0;
          const midpoint = attachment + limit / 2;
          return {
            midpoint,
            [`rate_${index}`]: numbers.parseFloatOrNull(layer.grossPPM),
            peerName: index === 0 ? "Current Submission" : peer.insured,
          };
        });
      } catch (error) {
        logger.error("Peer Pricing Layer Chart Error:", error);
        return [];
      }
    })
    .flat();

  const midpoints = new Set(layersData.map((item) => item.midpoint));
  const validIndices = new Set();

  const mergedData = Array.from(midpoints).map((midpoint) => {
    const dataPoint = { midpoint };
    peers.forEach((_, index) => {
      const peerLayer = layersData.find(
        (item) =>
          item.midpoint === midpoint && item[`rate_${index}`] !== undefined
      );
      if (peerLayer) {
        dataPoint[`rate_${index}`] = peerLayer[`rate_${index}`];
        dataPoint[`peer_${index}`] = peerLayer.peerName;
        validIndices.add(index);
      }
    });
    return dataPoint;
  });

  return {
    data: mergedData.sort((a, b) => a.midpoint - b.midpoint),
    validIndices,
  };
};

const PeerPricingLayerChart = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { peers } = props;

  const yLabel = translateLabel(
    labels.PEER_COMPARISON_TOWER_RATE_CHART_Y_LABEL,
    {
      default: "Rate Per Million",
    }
  );

  const xLabel = translateLabel(
    labels.PEER_COMPARISON_TOWER_RATE_CHART_X_LABEL,
    {
      default: "Layer Midpoint",
    }
  );

  const chart = parseData(peers);

  const {
    chartRef,
  } = chartUtils.useTooltipPositionerFixingBottomRightToChartTopRight();

  const hasValidData =
    chart &&
    chart.data &&
    Array.isArray(chart.data) &&
    chart.data.some(
      (midpointData) =>
        Object.entries(midpointData).filter(
          ([k, v]) => k.startsWith("rate") && v != null
        ).length > 0
    );

  if (!hasValidData) {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"100%"}
        padding={2}
      >
        <Typography variant={"h6"} color={"textSecondary"}>
          {translateLabel(labels.TOWER_RATE_CHART_NO_DATA, {
            default: "No Peer Data Available, or GWP missing",
          })}
        </Typography>
      </Box>
    );
  }

  const chartMargins = {
    top: 20,
    right: 30,
    left: 20,
    bottom: 5,
  };

  const defaultStyles = {
    position: "relative",
    width: "100%",
    paddingBottom: "500px",
  };

  return (
    <div style={defaultStyles}>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
        }}
      >
        <ResponsiveContainer width={"100%"} height={"100%"}>
          <ComposedChart data={chart.data} margin={chartMargins} ref={chartRef}>
            <CartesianGrid strokeDasharray={"3 3"} />

            <XAxis
              type={"number"}
              dataKey={"midpoint"}
              name={"Layer Midpoint"}
              domain={[0, "dataMax"]}
              tickFormatter={(value) => numbers.abbreviated(value)}
              label={{
                value: xLabel,
                position: "insideBottom",
                offset: -5,
              }}
            />

            <YAxis
              domain={[0, (dataMax) => numbers.roundUpNice(dataMax)]}
              tickFormatter={(value) => numbers.abbreviated(value)}
              label={{
                value: yLabel,
                angle: -90,
                position: "insideLeft",
              }}
            />

            <Tooltip
              isAnimationActive={false}
              formatter={(value, name) => {
                const peerIndex = name?.split?.("_")?.[1];
                if (!peerIndex || !chart?.data || !Array.isArray(chart.data)) {
                  return [numbers.abbreviated(value), "Unknown"];
                }
                const peerName = chart.data.find(
                  (item) => item?.[`peer_${peerIndex}`]
                )?.[`peer_${peerIndex}`];
                return [numbers.abbreviated(value), peerName || "Unknown"];
              }}
              labelFormatter={(label) =>
                `Midpoint: ${numbers.abbreviated(label ?? 0)}`
              }
            />

            <Legend
              verticalAlign={"top"}
              height={36}
              formatter={(value) => {
                const peerIndex = value?.split?.("_")?.[1];
                if (!peerIndex || !chart?.data || !Array.isArray(chart.data)) {
                  return value || "Unknown";
                }
                const peerName = chart.data.find(
                  (item) => item?.[`peer_${peerIndex}`]
                )?.[`peer_${peerIndex}`];
                return peerName || value;
              }}
            />

            {chart.validIndices &&
              Array.from(chart.validIndices).map((index) => (
                <Line
                  key={index}
                  type={"monotone"}
                  connectNulls={true}
                  dataKey={`rate_${index}`}
                  stroke={
                    index === 0
                      ? "#000000"
                      : COLORS[(index - 1) % COLORS.length]
                  }
                  name={`rate_${index}`}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  peers: peersSelectors.getPeers(state),
});

export default connect(mapStateToProps)(PeerPricingLayerChart);
